import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';

import TradeRuleTimeline from '../TradeRuleTimeline';

const TradeRuleShowPropertyFragment = graphql`
  fragment TradeRuleShow_property on Property @throwOnFieldError {
    uuid
    title
    ...TradeRuleTimeline_property
  }
`;

const TradeRuleShowRuleFragment = graphql`
  fragment TradeRuleShow_rule on TradeRule @throwOnFieldError {
    uuid
    ...TradeRuleTimeline_rule
  }
`;

/**
 * TradeRuleShow functional component displays a trade rule timeline for a property.
 * @param {object} props - Component props.
 * @param {object} props.property - Property fragment reference.
 * @param {object} props.rule - TradeRule fragment reference.
 * @returns {React.ReactElement} TradeRuleShow component.
 */
function TradeRuleShow({ property = null, rule = null }) {
  const intl = useIntl();
  const propertyData = useFragment(TradeRuleShowPropertyFragment, property);
  const ruleData = useFragment(TradeRuleShowRuleFragment, rule);

  if (!propertyData || !ruleData) {
    return <Loading />;
  }

  const { uuid, title } = propertyData;
  const pageTitle = intl.formatMessage(
    { id: 'trade_rule.trade_rule_show.page_title', defaultMessage: 'Enosi - Trade Rule {ruleId} for property {title}' },
    { ruleId: ruleData.uuid, title },
  );
  const propertiesBreadcrumb = intl.formatMessage({ id: 'trade_rule.trade_rule_show.breadcrumbs.properties.label', defaultMessage: ' Properties' });
  const tradeRulesBreadcrumb = intl.formatMessage({ id: 'trade_rule.trade_rule_show.breadcrumbs.trade_rules.label', defaultMessage: 'Trade Rules' });
  const tradeRuleHeader = intl.formatMessage({ id: 'trade_rule.trade_rule_show.header.trade_rule.label', defaultMessage: 'Trade Rule' });
  const showContext = intl.formatMessage({ id: 'trade_rule.trade_rule_show.header.context.show.label', defaultMessage: 'Show' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: propertiesBreadcrumb, path: '/properties' },
          { name: title, path: `/properties/${uuid}` },
          { name: tradeRulesBreadcrumb, path: `/properties/${uuid}/trade-rules` },
          { name: ruleData.uuid },
        ]}
      />
      <Heading
        title={tradeRuleHeader}
        context={showContext}
      />
      <TradeRuleTimeline property={propertyData} rule={ruleData} />
    </>
  );
}

TradeRuleShow.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  rule: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default TradeRuleShow;
